import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [cart, setCart] = useState({ items: [] });
  const [cartProducts, setCartProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && token) {
      fetchCart();
    } else {
      // Load from localStorage for guest users
      const savedCart = localStorage.getItem('guestCart');
      if (savedCart) {
        setCart(JSON.parse(savedCart));
      }
    }
  }, [user, token]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if ((cart?.items || []).length > 0) {
      fetchCartProducts();
    } else {
      setCartProducts([]);
    }
  }, [cart?.items]);

  const fetchCart = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/cart`);
      setCart(response.data);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCartProducts = async () => {
    try {
      const items = cart?.items || [];
      const productIds = [...new Set(items.map(item => item.product_id))];
      const products = await Promise.all(
        productIds.map(id => axios.get(`${API_URL}/products/${id}`).then(res => res.data).catch(() => null))
      );
      setCartProducts(products.filter(Boolean));
    } catch (error) {
      console.error('Failed to fetch cart products:', error);
    }
  };

  const addToCart = async (productId, variantIndex, quantity = 1) => {
    if (user && token) {
      try {
        await axios.post(`${API_URL}/cart/add`, { product_id: productId, variant_index: variantIndex, quantity });
        await fetchCart();
      } catch (error) {
        console.error('Failed to add to cart:', error);
        throw error;
      }
    } else {
      // Guest cart
      const newCart = { ...cart, items: cart?.items || [] };
      const existingItem = newCart.items.find(
        item => item.product_id === productId && item.variant_index === variantIndex
      );
      if (existingItem) {
        existingItem.quantity += quantity;
      } else {
        newCart.items.push({ product_id: productId, variant_index: variantIndex, quantity });
      }
      setCart(newCart);
      localStorage.setItem('guestCart', JSON.stringify(newCart));
    }
  };

  const updateCartItem = async (productId, variantIndex, quantity) => {
    if (user && token) {
      // Optimistic update: update local state immediately so UI reflects change instantly
      setCart(prev => {
        const newItems = quantity <= 0
          ? (prev.items || []).filter(i => !(i.product_id === productId && i.variant_index === variantIndex))
          : (prev.items || []).map(i =>
              i.product_id === productId && i.variant_index === variantIndex
                ? { ...i, quantity }
                : i
            );
        return { ...prev, items: newItems };
      });

      try {
        await axios.put(`${API_URL}/cart/update`, { product_id: productId, variant_index: variantIndex, quantity });
      } catch (error) {
        console.error('Failed to update cart:', error);
        // Revert by re-fetching from server on failure
        await fetchCart();
        throw error;
      }
    } else {
      const newCart = { ...cart };
      if (quantity <= 0) {
        newCart.items = newCart.items.filter(
          item => !(item.product_id === productId && item.variant_index === variantIndex)
        );
      } else {
        const item = newCart.items.find(
          item => item.product_id === productId && item.variant_index === variantIndex
        );
        if (item) {
          item.quantity = quantity;
        }
      }
      setCart(newCart);
      localStorage.setItem('guestCart', JSON.stringify(newCart));
    }
  };

  const removeFromCart = async (productId, variantIndex) => {
    await updateCartItem(productId, variantIndex, 0);
  };

  const clearCart = async () => {
    if (user && token) {
      try {
        await axios.delete(`${API_URL}/cart/clear`);
        setCart({ items: [] });
      } catch (error) {
        console.error('Failed to clear cart:', error);
      }
    } else {
      setCart({ items: [] });
      localStorage.removeItem('guestCart');
    }
  };

  const getCartTotal = () => {
    return (cart?.items || []).reduce((total, item) => {
      const product = cartProducts.find(p => p.id === item.product_id);
      if (product && product.variants[item.variant_index]) {
        return total + (product.variants[item.variant_index].price * item.quantity);
      }
      return total;
    }, 0);
  };

  const getCartItemCount = () => {
    return (cart?.items || []).reduce((count, item) => count + item.quantity, 0);
  };

  return (
    <CartContext.Provider value={{
      cart,
      cartProducts,
      loading,
      addToCart,
      updateCartItem,
      removeFromCart,
      clearCart,
      getCartTotal,
      getCartItemCount,
      fetchCart
    }}>
      {children}
    </CartContext.Provider>
  );
};
